import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { PaginationParams } from '@/types/params'
import { formatApiDate } from '@/utils/format-date'

import { schoolBreakSchema } from '../constants/school-break-schemas'
import type { SchoolBreak, SchoolYearAndBreakToggle } from '../types'

const responseSchema = z.array(schoolBreakSchema)

type APIResponse = z.infer<typeof responseSchema>

const parseResponse = (response: APIResponse): SchoolBreak[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    status: item.status,
    startDate: item.start_date,
    endDate: item.end_date
  }))

type Filters = {
  year?: string[]
  dateAfter?: string
  dateBefore?: string
  search?: string
  status?: SchoolYearAndBreakToggle
}

export type SchoolBreaksParams = Filters & PaginationParams

export const useSchoolBreaks = (params: SchoolBreaksParams = {}) =>
  useQuery({
    queryKey: ['panelSchoolBreaksList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelSchoolBreaksList({
        page: params.page ?? 1,
        year: params.year,
        ends_after: params.dateAfter
          ? formatApiDate(new Date(params.dateAfter))
          : undefined,
        starts_before: params.dateBefore
          ? formatApiDate(new Date(params.dateBefore))
          : undefined,
        // @ts-expect-error Waits for backend fix
        page_size: params.pageSize ?? DEFAULT_PAGE_SIZE,
        status: params.status === 'past' ? ['past'] : ['active', 'future'],
        search: params.search,
        ordering: params.status === 'past' ? '-start_date' : 'start_date',
        fetchKeys: {
          id: true,
          name: true,
          start_date: true,
          end_date: true,
          status: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return { list: parsed, count: response.count }
    }
  })
